/**
 * Guide Commentary Component
 *
 * Displays contextual commentary from the user's selected guide.
 * Used throughout the app to make the guide feel present.
 *
 * Australian English throughout.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import { useGuide } from '../../context/GuideContext';

/**
 * Context-specific guide messages
 * Each guide has unique commentary for different app contexts
 */
const GUIDE_COMMENTARY = {
  // Modules page introduction
  modulesIntro: {
    sage: "I'm curious to see which patterns resonate with you. Each module reveals something different about how friendships work — let's explore together.",
    jaz: "These modules are actually good. Pick whatever speaks to you right now, and let's build some real skills.",
    mika: "Ooh, this is exciting! Each module is backed by actual research about how our brains handle friendships. Ready to geek out with me?",
    riley: "Take your time here. Like seasons, each skill has its moment. Choose what feels right for where you are now.",
    alex: "I've been through all of this stuff myself. Some modules hit harder than others — pick what you need most right now.",
    sam: "There's no rush and no wrong choice here. Whatever you're drawn to is exactly where you need to start.",
  },

  // Assessment results reaction - celebrating
  assessmentCelebrate: {
    sage: "These results show some fascinating patterns. I noticed some real strengths here — let's think about how to build on them.",
    jaz: "Look at these results. You've got some serious strengths showing up here. Don't skip past this win.",
    mika: "Okay, this is actually fascinating! Your brain has developed some really interesting patterns. Let me break down what I'm seeing...",
    riley: "These results tell a story about where you've been and where you're growing. I see real potential here.",
    alex: "Hey, these results are actually pretty solid. I remember when I first saw mine — it's wild to see your patterns laid out like this.",
    sam: "Taking this assessment takes courage. These results show you have real strengths to build on.",
  },

  // Assessment results - areas for growth
  assessmentGrowth: {
    sage: "The growth areas aren't weaknesses — they're just patterns you haven't optimised yet. There's logic to why these developed.",
    jaz: "Growth areas aren't failures. They're just the next level. We can work on this together.",
    mika: "Your brain is built to get better at this. That's what neuroplasticity means — these patterns can change.",
    riley: "Growth doesn't happen all at once. These areas are seeds waiting for the right conditions. We'll nurture them together.",
    alex: "I had similar growth areas when I started. They do get better with practice.",
    sam: "It makes complete sense that you'd have some areas still developing. That's not a flaw — it's just part of the journey.",
  },

  // Module completion encouragement
  moduleComplete: {
    sage: "I noticed you completed that whole module. That kind of consistent effort is how real understanding develops.",
    jaz: "Yes! Module done! That's HUGE progress. Seriously — give yourself credit for showing up and doing the work.",
    mika: "Your brain just built new neural pathways! Every module you complete literally changes how you process friendships.",
    riley: "Another step on the path. Like adding rings to a tree, each completed module makes you stronger.",
    alex: "Nice work finishing that! I know some of it might have hit close to home — that's how you know it's working.",
    sam: "I'm proud of you for completing that module. It takes real commitment to do this kind of self-reflection.",
  },

  // Dashboard greeting
  dashboardGreeting: {
    sage: "Welcome back. I've been thinking about your progress — there are some interesting patterns emerging.",
    jaz: "Hey, you're back! Let's see what we can work on today. Ready to keep building those friendship skills?",
    mika: "Great to see you. Consistent practice like this is how your brain actually changes. Science is cool.",
    riley: "Welcome back to the journey. Each return is like the sun rising again — a new chance to grow.",
    alex: "Oh hey! Good to see you. I was just thinking about how far you've come since you started.",
    sam: "I'm glad you're here. However you're feeling today, this is a good space to be.",
  },
};

/**
 * Get commentary for a specific context
 */
export const getGuideCommentary = (guideId, context) => {
  const contextMessages = GUIDE_COMMENTARY[context];
  if (!contextMessages) return null;
  return contextMessages[guideId] || contextMessages.sage; // Default to sage
};

/**
 * GuideCommentary Component
 *
 * @param {string} context - The context for the commentary (e.g., 'modulesIntro', 'assessmentCelebrate')
 * @param {string} customMessage - Optional custom message to override context-based message
 * @param {string} variant - 'bubble' (speech bubble style) or 'card' (card style) or 'inline' (minimal)
 * @param {boolean} animate - Whether to animate the entrance
 * @param {string} className - Additional CSS classes
 */
const GuideCommentary = ({
  context,
  customMessage,
  variant = 'bubble',
  animate = true,
  className = '',
}) => {
  const { selectedGuide, hasCompletedSelection } = useGuide();

  // Don't render if no guide selected
  if (!hasCompletedSelection) return null;

  const message = customMessage || getGuideCommentary(selectedGuide.id, context);
  if (!message) return null;

  const guide = selectedGuide;

  const Wrapper = animate ? motion.div : 'div';
  const animationProps = animate
    ? {
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.4 },
      }
    : {};

  // Bubble variant - speech bubble style
  if (variant === 'bubble') {
    return (
      <Wrapper {...animationProps} className={className}>
        <div className="flex items-start gap-3">
          {/* Guide Avatar */}
          <div
            className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold shadow-lg"
            style={{ backgroundColor: guide.accentColor }}
          >
            {guide.name[0]}
          </div>

          {/* Speech Bubble */}
          <div className="flex-1">
            <div
              className="relative bg-slate-800/80 rounded-2xl rounded-tl-md px-4 py-3 border border-slate-700/50"
            >
              {/* Pointer */}
              <div
                className="absolute -left-2 top-3 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8"
                style={{ borderRightColor: 'rgb(30 41 59 / 0.8)' }}
              />

              <p className="text-slate-200 text-sm leading-relaxed">
                {message}
              </p>

              <p
                className="text-xs mt-2 font-medium"
                style={{ color: guide.accentColor }}
              >
                — {guide.name}
              </p>
            </div>
          </div>
        </div>
      </Wrapper>
    );
  }

  // Card variant - full card style
  if (variant === 'card') {
    return (
      <Wrapper {...animationProps} className={className}>
        <div
          className="rounded-xl p-4 border"
          style={{
            backgroundColor: `${guide.accentColor}10`,
            borderColor: `${guide.accentColor}30`,
          }}
        >
          <div className="flex items-start gap-3">
            <div
              className="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center text-white font-bold text-lg shadow-lg"
              style={{ backgroundColor: guide.accentColor }}
            >
              {guide.name[0]}
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-white">{guide.name}</span>
                <span className="text-xs text-slate-400">Your Guide</span>
              </div>
              <p className="text-slate-200 leading-relaxed">
                {message}
              </p>
            </div>
          </div>
        </div>
      </Wrapper>
    );
  }

  // Inline variant - minimal style
  if (variant === 'inline') {
    return (
      <Wrapper {...animationProps} className={`flex items-center gap-2 ${className}`}>
        <div
          className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold"
          style={{ backgroundColor: guide.accentColor }}
        >
          {guide.name[0]}
        </div>
        <p className="text-slate-300 text-sm italic">
          &ldquo;{message}&rdquo;
        </p>
      </Wrapper>
    );
  }

  return null;
};

export default GuideCommentary;
